import React, { useCallback } from 'react';
import { formatTranslation } from 'intl';
import { InspectionTemplateTranslationKeys } from 'intl/keys';
import {
  DndContext,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Box, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DeleteIcon from '@mui/icons-material/Delete';
import { useIntl } from 'react-intl';
import { useTreeBuilder } from '../../hooks/useTreeBuilder';
import { useDragAndDrop } from '../../hooks/useDragAndDrop';
import {
  TreeNode,
  isSectionNode,
  findNodeById,
  flattenTree,
} from 'shared/domain/checklistTree';
import { SortableTreeItem } from '../sortableTreeItem';
import { MemoSectionNode } from '../sectionNode';
import { MemoChecklistItemNode } from '../checklistItemNode';
import {
  BuilderContainer,
  AddSectionButton,
  AddButtonsContainer,
  DRAG_OVERLAY_SX,
  DRAG_OVERLAY_ROW_SX,
  DRAG_OVERLAY_NUMBER_SX,
} from './styles';
import { TemplateBuilderProps } from './types';

export function TemplateBuilder({
  sections,
  items,
  onReorderSection,
  onReorderItem,
  onSectionTitleChange,
  onSectionDescriptionChange,
  onSectionGraphicUploaderInit,
  onDeleteSection,
  onItemSummaryChange,
  onItemDescriptionChange,
  onItemGraphicUploaderInit,
  onDeleteItem,
  onAddSection,
  onAddItem,
  sectionErrors,
  sectionDefaultValues,
  sectionGraphicUploaders,
  itemErrors,
  itemDefaultValues,
  itemGraphicUploaders,
  itemAnswers,
  onAddAnswer,
  onUpdateAnswer,
  onDeleteAnswer,
  checklistValidationFocus,
}: TemplateBuilderProps): React.ReactElement {
  const intl = useIntl();
  const { tree } = useTreeBuilder({
    sections,
    items,
  });

  const { activeId, handleDragStart, handleDragOver, handleDragEnd, handleDragCancel } =
    useDragAndDrop({
      tree,
      onReorderSection,
      onReorderItem,
    });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const renderNode = useCallback(
    (node: TreeNode, index: number): React.ReactElement => {
      if (isSectionNode(node)) {
        return (
          <SortableTreeItem key={node.id} node={node}>
            {(dragHandleProps) => (
              <MemoSectionNode
                node={node}
                onTitleChange={onSectionTitleChange}
                onDescriptionChange={onSectionDescriptionChange}
                onGraphicUploaderInit={onSectionGraphicUploaderInit}
                onDelete={onDeleteSection}
                onAddItem={onAddItem}
                onAddSubsection={onAddSection}
                errors={sectionErrors?.[node.id]}
                defaultValues={sectionDefaultValues?.[node.id]}
                graphicUploader={sectionGraphicUploaders?.[node.id]}
                dragHandleProps={dragHandleProps}
              >
                {node.children.map((child, childIndex) => renderNode(child, childIndex))}
              </MemoSectionNode>
            )}
          </SortableTreeItem>
        );
      }

      return (
        <SortableTreeItem key={node.id} node={node}>
          {(dragHandleProps) => (
            <MemoChecklistItemNode
              node={node}
              onSummaryChange={onItemSummaryChange}
              onDescriptionChange={onItemDescriptionChange}
              onGraphicUploaderInit={onItemGraphicUploaderInit}
              onDelete={onDeleteItem}
              errors={itemErrors?.[node.id]}
              defaultValues={itemDefaultValues?.[node.id]}
              graphicUploader={itemGraphicUploaders?.[node.id]}
              dragHandleProps={dragHandleProps}
              index={index}
              answers={itemAnswers?.[node.id]}
              onAddAnswer={onAddAnswer}
              onUpdateAnswer={onUpdateAnswer}
              onDeleteAnswer={onDeleteAnswer}
              validationFocus={checklistValidationFocus}
            />
          )}
        </SortableTreeItem>
      );
    },
    [
      onSectionTitleChange,
      onSectionDescriptionChange,
      onSectionGraphicUploaderInit,
      onDeleteSection,
      onItemSummaryChange,
      onItemDescriptionChange,
      onItemGraphicUploaderInit,
      onDeleteItem,
      onAddItem,
      onAddSection,
      sectionErrors,
      sectionDefaultValues,
      sectionGraphicUploaders,
      itemErrors,
      itemDefaultValues,
      itemGraphicUploaders,
      itemAnswers,
      onAddAnswer,
      onUpdateAnswer,
      onDeleteAnswer,
      checklistValidationFocus,
    ]
  );

  const sortableIds = flattenTree(tree).map((node) => node.id);
  const activeNode = activeId ? findNodeById(tree, String(activeId)) : null;
  const activeNodeTitle = activeNode
    ? isSectionNode(activeNode)
      ? activeNode.data.title ||
        formatTranslation(intl, InspectionTemplateTranslationKeys.wizardSectionDefaultTitle)
      : activeNode.data.summary ||
        formatTranslation(intl, InspectionTemplateTranslationKeys.wizardItemPlaceholder)
    : '';

  return (
    <BuilderContainer>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext
          items={sortableIds}
          strategy={verticalListSortingStrategy}
        >
          {tree.map((node, index) => renderNode(node, index))}
        </SortableContext>

        <DragOverlay>
          {activeNode ? (
            <Box sx={DRAG_OVERLAY_SX}>
              <Box sx={DRAG_OVERLAY_ROW_SX}>
                <Box component="span" sx={{ display: 'inline-flex', color: 'text.secondary' }}>
                  <DragHandleIcon fontSize="small" />
                </Box>
                <Box component="span" sx={{ display: 'inline-flex', color: 'text.secondary' }}>
                  <ExpandLessIcon fontSize="small" />
                </Box>
                <Typography variant="body1" sx={DRAG_OVERLAY_NUMBER_SX}>
                  {activeNode.displayNumber}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontWeight: isSectionNode(activeNode) ? 700 : 500,
                    color: 'text.primary',
                  }}
                >
                  {activeNodeTitle}
                </Typography>
                <Box component="span" sx={{ display: 'inline-flex', color: 'text.primary' }}>
                  <DeleteIcon fontSize="small" />
                </Box>
              </Box>
            </Box>
          ) : null}
        </DragOverlay>
      </DndContext>

      {(onAddSection || onAddItem) && (
        <AddButtonsContainer>
          {onAddSection && (
            <AddSectionButton
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => onAddSection()}
            >
              {formatTranslation(intl, InspectionTemplateTranslationKeys.wizardAddSection)}
            </AddSectionButton>
          )}
          {onAddItem && (
            <AddSectionButton
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => onAddItem()}
            >
              {formatTranslation(intl, InspectionTemplateTranslationKeys.wizardAddItem)}
            </AddSectionButton>
          )}
        </AddButtonsContainer>
      )}
    </BuilderContainer>
  );
}

export const MemoTemplateBuilder = React.memo(TemplateBuilder);
export default TemplateBuilder;
